import React, { useState, useEffect } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Typography, Box, CircularProgress, Alert,
} from "@mui/material";
import { PersonAdd } from "@mui/icons-material";
import { addAuthorizedUser } from "../../services/tenantService";

const AddAuthorizedUserDialog = ({ open, onClose, propertyId, onUserAdded, addedBy }) => {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!open) {
      setEmail("");
      setError("");
      setSuccess(false);
    }
  }, [open]);

  const validate = () => {
    if (!email.trim()) return "Email is required.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
      return "Please enter a valid email address.";
    return null;
  };

  const handleSubmit = async () => {
    const validationError = validate();
    if (validationError) { setError(validationError); return; }

    setSubmitting(true);
    setError("");

    try {
      const newUser = await addAuthorizedUser(propertyId, email.trim(), addedBy);
      setSuccess(true);
      onUserAdded?.(newUser);
    } catch (err) {
      setError(err.message || "Failed to add user. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Add Authorized User</DialogTitle>
      <DialogContent>
        {success ? (
          <Box sx={{ py: 1 }}>
            <Alert severity="success" sx={{ mb: 2 }}>
              User added successfully!
            </Alert>
            <Typography variant="body2" color="text.secondary">
              <strong>{email}</strong> has been added as an authorized user.
              Their access is pending approval.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            {error && (
              <Alert severity="error" onClose={() => setError("")}>
                {error}
              </Alert>
            )}
            <Typography variant="body2" color="text.secondary">
              This user will be added with <strong>pending</strong> status and
              must be approved before gaining access.
            </Typography>
            <TextField
              label="Email Address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="user@example.com"
              fullWidth
              required
              autoFocus
              onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {success ? (
          <Button onClick={onClose} variant="contained">Done</Button>
        ) : (
          <>
            <Button onClick={onClose} disabled={submitting}>Cancel</Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={submitting}
              startIcon={submitting ? <CircularProgress size={16} /> : <PersonAdd />}
            >
              {submitting ? "Adding..." : "Add User"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AddAuthorizedUserDialog;